import {
  OmniElement,
  OmniStyle,
  OmniIcon,
  OmniTooltip,
  html,
  css,
  repeat,
} from 'omni-ui';
import { om2alert, om2confirm } from './om2-alert.js';
import { om2FormModal } from './om2-form-modal.js';
import { Om2Table } from './om2-table.js';
import { Om2IconMenu } from './om2-icon-menu.js';
import { downloadFile, getSSOEnv } from './om2-util.js';
import { metadata, schemaFieldType } from './om2-form-builder-metadata.js';

OmniStyle.register();
OmniIcon.register();
OmniTooltip.register();
Om2Table.register();
Om2IconMenu.register();

/* eslint-disable class-methods-use-this */

export class Om2FormBuilder extends OmniElement {
  constructor() {
    super();
    // Used to share some info between all the nested form builder elements
    this.sharedState = {};
    this.preview = false;
    this.layoutHidden = true;
    this.isRoot = true;
    this.env = getSSOEnv();

    this.addEventListener('ref-deleted', e => {
      if (this.uiSchema.type === 'ref' && this.uiSchema.id === e.detail) {
        this._deleteLayout(false); // Delete this part of the layout as the reference is invalid now
      }
      // Send event to all children too
      this.shadowRoot.querySelectorAll('om2-form-builder').forEach(elem => {
        elem.dispatchEvent(
          new CustomEvent('ref-deleted', { detail: e.detail })
        );
      });
    });
  }

  /**
   * @property {function} save - save callback
   * @property {object} schema - The current Om2Form schema object
   * @property {object} uiSchema - The current Om2Form uiSchema object
   * @property {string} env - The environment: dev, qa etc
   * @property {string} enabledSharedFields - Whether to add a "shared" field checkbox when creating input fields. It's the responsibility of application code to handle saving and restoring these fields in a "shared" location.
   * @property {array} includeFields - Explicit list of fields to include that are otherwise excluded by default (e.g. "file" input field). List is comma separated.
   * @property {function} filterFieldSchema - Function that determines which parts of an input field's schema should be shown in add/edit/clone modal.
   * ...
   */
  static get properties() {
    return {
      save: { type: Function },
      schema: { type: Object },
      uiSchema: { type: Object },
      env: { type: String },
      enableSharedFields: { type: Boolean },
      includeFields: { type: String },
      filterFieldSchema: { type: Function },

      // The following are internally used not to be passed or accessed by users
      layoutHidden: { type: Boolean, reflect: true },
      fieldsHidden: { type: Boolean, reflect: true },
      position: { type: Number },
      maxPosition: { type: Number },
      sharedState: { attribute: false },
      tableData: { attribute: false },
      preview: { attribute: false },
      isRoot: { attribute: false },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        :host-context([type='column']) {
          display: inline-block;
        }

        .omni span.box-type,
        .omni span.input-fields {
          margin-right: 5px;
          font-weight: bold;
          color: var(--color-almost-black);
        }

        :host-context([layouthidden]) .omni span.box-type {
          color: var(--color-shark);
        }

        :host-context([fieldshidden]) .omni span.input-fields {
          color: var(--color-shark);
        }

        .box.highlighted {
          box-shadow: rgba(1, 33, 78, 0.3) 0px 10px 60px -5px;
          z-index: 1;
          position: relative;
        }

        .plusminus {
          position: relative;
          left: -5px;
          color: var(--color-shark);
          font-size: medium;
        }
        .plusminus:hover {
          color: var(--color-electric-blue);
        }

        omni-icon[icon-id='omni:interactive:actions']:hover {
          fill: var(--color-electric-blue);
        }

        .omni .dropdown .dropdown-menu .dropdown-content .dropdown-item:hover {
          background-color: var(--color-pale-grey);
        }
      `,
    ];
  }

  static register(name = 'om2-form-builder') {
    return super.register(name, Om2FormBuilder);
  }

  firstUpdated() {
    // These should be initialized by now, but if not we can fix it here
    if (!this.schema) this.schema = {};
    if (!this.uiSchema) this.uiSchema = [];
  }

  get _meta() {
    return metadata.layout[this.isRoot ? 'root' : this.uiSchema.type];
  }

  /**
   * Visually highlight a box in our layout on mouseover/mouseout
   *
   * @param {event} event
   */
  _highlightBox(event) {
    if (event?.target && !event.target.classList.contains('box')) return;

    if (this.sharedState.highlighted) {
      this.sharedState.highlighted.classList.remove('highlighted');
      this.sharedState.highlighted = null;
    }

    if (event?.target) {
      this.sharedState.highlighted = event?.target;
      this.sharedState.highlighted.classList.add('highlighted');
    }
  }

  /**
   * Convert nested property names (foo.bar.baz) to nested data structure.
   * We update target for each key/value pair.
   *
   * @param {object} data - source data structure containing keys with periods that should be expanded to a nested structure
   * @param {object} [target] - data structure to apply over (default {})
   * @returns {object} Returns merged nested data structure representation
   */
  _expandFlattenedProps(data, target = {}) {
    Object.entries(data).forEach(([originalProp, value]) => {
      const props = originalProp.split('.');
      let parent = target;
      const lastProp = props.pop();
      props.forEach(prop => {
        if (!parent[prop]) parent[prop] = {};
        parent = parent[prop];
      });
      parent[lastProp] = value;
    });
    return target;
  }

  /**
   * Similar to _expandFlattenedProps, but here given an array of keys we need to get
   * the corresponding values from source taking into account the keys may need to
   * be expanded and pulled from the nested structure of source.
   *
   * @param {array|object} props - Defines the [nested] properties
   * @param {object} source - Source data to pull values from that correspond to properties
   * @param {boolean} [removeUndefined] - If the results should have undefined values filtered out
   * @returns {object} The resulting object mapping of (property => value)
   */
  _extractFlattenedProps(props, source, removeUndefined = false) {
    let newProps = props;
    if (!Array.isArray(newProps)) {
      newProps = Object.keys(newProps);
    }
    return Object.fromEntries(
      newProps
        .map(prop => {
          let value = source;
          prop.split('.').forEach(p => {
            value = value?.[p];
          });
          return [prop, value];
        })
        .filter(([, v]) => !removeUndefined || v !== undefined)
    );
  }

  /**
   * Open modal to add a new layout type.
   *
   * @param {string} type - The layout type to add
   */
  _addLayout(type) {
    const meta = metadata.layout[type];
    const data = Object.fromEntries(
      Object.entries(meta.fields)
        .map(([name, field]) => [name, field.default])
        .filter(([, v]) => v !== undefined)
    );
    const fields = structuredClone(meta.fields);

    // Add list of available input fields if type is ref
    if (type === 'ref') {
      fields.id.enum = Object.entries(this.schema).map(([key, value]) => ({
        name: `${value.title ?? ''} (${key})`,
        id: key,
      }));
    }

    const pushLayout = layoutData => {
      // eslint-disable-next-line no-param-reassign
      if (metadata.layout[type].maxContents > 0) layoutData.content = [];
      if (Array.isArray(this.uiSchema)) {
        this.uiSchema.push(layoutData);
      } else {
        if (!this.uiSchema.content) this.uiSchema.content = [];
        this.uiSchema.content.push(layoutData);
      }
    };

    // For layout types with no required fields, skip the modal
    const skipModal = Object.entries(fields).every(
      ([, field]) => !field.required
    );
    if (skipModal) {
      pushLayout({ type });
      this.requestUpdate();
      return;
    }

    om2FormModal({
      title: `Add ${type}`,
      data,
      env: this.env,
      onSubmit: submitData => {
        /* eslint-disable no-param-reassign */
        submitData = this._expandFlattenedProps(submitData);
        submitData.type = type;
        /* eslint-enable no-param-reassign */
        pushLayout(submitData);
      },
      onSuccess: () => {
        this.requestUpdate();
      },
      schema: fields,
    });
  }

  /**
   * Edit the current layout object
   *
   */
  _editLayout() {
    const data = this._extractFlattenedProps(
      this._meta.fields,
      this.uiSchema,
      true
    );
    const fields = structuredClone(this._meta.fields);

    // Add list of available input fields if type is ref
    if (this.uiSchema.type === 'ref') {
      fields.id.enum = Object.entries(this.schema).map(([key, value]) => ({
        name: value.title ?? key,
        id: key,
      }));
    }

    om2FormModal({
      title: `Edit ${this.uiSchema.type}`,
      data,
      env: this.env,
      onSubmit: submitData =>
        this._expandFlattenedProps(submitData, this.uiSchema),
      onSuccess: () => this.requestUpdate(),
      schema: fields,
    });
  }

  /**
   * Delete the current layout object
   *
   */
  _deleteLayout(confirm = true) {
    if (confirm) {
      om2confirm(`Delete this ${this.uiSchema.type} from the layout?`, {
        type: 'warning',
        callback: () => this.dispatchNewEvent('delete'),
      });
    } else {
      this.dispatchNewEvent('delete');
    }
  }

  /**
   * Move the current layout object's position
   *
   */
  _moveLayout() {
    // Adjust position to make this 1-based index (vs. 0) when displaying to user
    om2FormModal({
      title: `Move ${this.uiSchema.type} to...`,
      data: { position: this.position + 1 },
      env: this.env,
      onSubmit: data =>
        this.dispatchNewEvent('move', {
          detail: { position: data.position - 1 },
        }),
      onSuccess: () => {},
      schema: {
        position: {
          title: 'Position',
          type: 'number',
          min: 1,
          max: this.maxPosition + 1,
          multipleOf: 1,
        },
      },
    });
  }

  /**
   * Open modal to add new input field
   *
   * @param {string} type - the input field type
   * @param {object} fieldMeta - Metadata for the field
   * @throws {Error} - If the id chosen already exists
   */
  _addInputField(type, fieldMeta, cloneData) {
    om2FormModal({
      title: `${cloneData ? 'Clone' : 'Add'} ${fieldMeta.label}`,
      data: cloneData ?? {},
      env: this.env,
      onSubmit: submitData => {
        /* eslint-disable no-param-reassign */
        submitData = this._expandFlattenedProps(submitData);
        submitData.type = type;
        if (!submitData.sharedField) delete submitData.sharedField;
        Object.entries(fieldMeta.overrides ?? {}).forEach(([key, value]) => {
          submitData[key] = value;
        });
        const { id } = submitData;
        delete submitData.id;
        /* eslint-enable no-param-reassign */
        if (this.schema[id])
          throw new Error(`Input field id ${id} already exists`);
        this.schema[id] = submitData;
      },
      onSuccess: () => this.requestUpdate(),
      schema: this._addSharedSchema(fieldMeta.fields, type, true),
    });
  }

  _cloneInputField(id) {
    const fieldSchema = this.schema[id];
    const type = schemaFieldType(fieldSchema);
    const fieldMeta = structuredClone(metadata.fields[type]);
    const cloneData = this._extractFlattenedProps(
      fieldMeta.fields,
      fieldSchema,
      true
    );
    cloneData.id = id;
    this._addInputField(type, fieldMeta, cloneData);
  }

  /**
   * Open modal to edit an input field
   *
   * @param {string} id
   */
  _editInputField(id) {
    // The schema of the current input field
    const fieldSchema = this.schema[id];
    // Metadata about this schema type so we know how to edit it
    const fieldMeta = structuredClone(
      metadata.fields[schemaFieldType(fieldSchema)]
    );

    // Don't allow changing the id during edit
    Object.entries(fieldMeta.fields).forEach(([name, field]) => {
      // eslint-disable-next-line no-param-reassign
      if (name === 'id') field.readonly = true;
    });
    const data = this._extractFlattenedProps(
      this._addSharedSchema(
        fieldMeta.fields,
        schemaFieldType(fieldSchema),
        false
      ),
      fieldSchema,
      true
    );
    data.id = id;
    om2FormModal({
      title: `Edit ${fieldMeta.label}`,
      data,
      env: this.env,
      onSubmit: submitData =>
        this._expandFlattenedProps(submitData, fieldSchema),
      onSuccess: () => this.requestUpdate(),
      schema: this._addSharedSchema(
        fieldMeta.fields,
        schemaFieldType(fieldSchema),
        false
      ),
    });
  }

  /**
   * Delete an input field
   *
   * @param {string} id
   */
  _deleteInputField(id) {
    om2confirm(
      `Delete Input Field ${id} and all references to it in the layout?`,
      {
        type: 'warning',
        callback: () => {
          delete this.schema[id];
          this.dispatchEvent(new CustomEvent('ref-deleted', { detail: id }));
          this.requestUpdate();
        },
      }
    );
  }

  /**
   * Helper function to add a "Shared" checkbox option for input fields when adding/editing, if the feature is enabled.
   *
   * @param {Object} baseSchema - The base schema object
   * @param {boolean} isAddAction - Flag whether adding (or editing) an input field
   * @returns {Object} The updated schema object
   */
  _addSharedSchema(baseSchema, fieldType, isAddAction) {
    if (this.filterFieldSchema)
      // eslint-disable-next-line no-param-reassign
      baseSchema = Object.fromEntries(
        Object.entries(baseSchema).filter(([id]) =>
          this.filterFieldSchema(fieldType, id, isAddAction)
        )
      );

    return this.enableSharedFields
      ? {
          ...baseSchema,
          sharedField: {
            type: 'boolean',
            title: 'Share Input Data',
            disabled: !isAddAction, // disable if edit
          },
        }
      : baseSchema;
  }

  /**
   * Sends event to indicate that the builder was canceled.
   */
  _cancel(e) {
    e?.preventDefault();
    this.dispatchNewEvent('om2-form-builder-cancel');
  }

  /**
   * Wrapper around this.save() callback that passes data to save with an alert.
   *
   * @async
   */
  async _triggerSave() {
    try {
      await this.save({
        uiSchema: this.uiSchema,
        schema: this.schema,
      });
    } catch (e) {
      om2alert(e);
    }
  }

  /**
   * Save internal state of form (fields & layout) as a JSON file
   *
   */
  _exportForm() {
    const json = JSON.stringify(
      {
        schema: this.schema,
        uiSchema: this.uiSchema,
        date: new Date().toISOString(),
        by: window.AN?.sso?.user?.email,
      },
      null,
      2
    );

    om2FormModal({
      title: 'Export Form',
      schema: {
        filename: {
          title: 'Filename',
          type: 'string',
          required: true,
          default: 'form-builder-export',
          formModal: { fullWidth: true },
        },
      },
      submitButtonText: 'Export',
      onSubmit: ({ filename }) => {
        downloadFile(json, `${filename}.json`);
      },
    });
  }

  /**
   * Load state from JSON file
   *
   */
  _importForm() {
    om2FormModal({
      title: 'Import Form',
      schema: {
        file: {
          title: 'Filename',
          type: 'file',
          accept: '.json',
          required: true,
          formModal: { fullWidth: true },
        },
      },
      submitButtonText: 'Import',
      onSubmit: async ({ file }) => {
        const json = await file.text();
        const { schema, uiSchema } = JSON.parse(json);
        const doImport = async () => {
          // Render as empty form first to avoid any weirdness
          this.schema = {};
          this.uiSchema = [];
          await this.requestUpdate();

          this.schema = schema;
          this.uiSchema = uiSchema;
          this.sharedState = {};
        };

        if (Object.keys(this.schema).length > 0 || this.uiSchema?.length > 0) {
          om2confirm(
            'Replace all fields and layout items with imported data?',
            {
              type: 'danger',
              callback: doImport,
            }
          );
        } else {
          doImport(); // Nothing to lose no need to confirm
        }
      },
    });
  }

  _filteredInputFields() {
    const explicitInclude = (this.includeFields ?? '').split(',');
    return Object.fromEntries(
      Object.entries(metadata.fields).filter(
        ([key, field]) => !field.exclude || explicitInclude.includes(key)
      )
    );
  }

  /**
   * Render all input fields in a table
   *
   * @returns {html} rendered output
   */
  _renderInputFields() {
    if (!this.isRoot) return '';

    // Re-create table every render in case this.schema has been edited. Only the root
    // om2-form-builder object creates the table.
    this.tableData = Object.entries(this.schema).map(([id, field]) => ({
      ...field,
      id,
      schemaType: schemaFieldType(field, true),
    }));

    const actions = [
      { label: 'Export Form', value: '_exportForm' },
      { label: 'Import Form', value: '_importForm' },
    ];

    return html`
      <div class="box mb-2">
        <om2-icon-menu
          class="is-pulled-right is-right mb-1 is-small"
          iconId="omni:interactive:actions"
          iconClass="is-size-3"
          tooltip="Actions"
          .options="${actions}"
          @change="${e => this[e.detail.value.value]()}"
        >
        </om2-icon-menu>

        <span
          class="is-clickable plusminus"
          @click=${() => {
            this.fieldsHidden = !this.fieldsHidden;
          }}
          @keyup=${e => {
            if (['Enter', ' '].includes(e.key))
              this.fieldsHidden = !this.fieldsHidden;
          }}
          >${this.fieldsHidden ? html`&plusb;` : html`&boxminus;`}
        </span>
        <span class="input-fields">Input Fields</span>
        ${this.fieldsHidden
          ? ''
          : html`
              <om2-table
                autosort
                .columns=${[
                  {
                    label: 'Id',
                    key: 'id',
                    isSortable: true,
                    passthrough: true,
                    template: row =>
                      html`<td>
                        ${row.sharedField
                          ? html` <omni-tooltip>
                              <i>${row.id}</i>
                              <div slot="content">Shared Input Field</div>
                            </omni-tooltip>`
                          : html`${row.id}`}
                      </td>`,
                  },
                  {
                    label: 'Title',
                    key: 'title',
                    isSortable: true,
                  },
                  {
                    label: 'Type',
                    key: 'schemaType',
                    isSortable: true,
                  },
                  {
                    label: 'Required',
                    key: 'required',
                    isSortable: true,
                    template: val =>
                      html`<td>
                        ${val
                          ? html`<omni-icon
                              class="is-size-2"
                              icon-id="omni:informative:check"
                            ></omni-icon>`
                          : ''}
                      </td>`,
                  },
                  {
                    label: 'Actions',
                    passthrough: true,
                    template: val => html`
                      <td>
                        <button
                          class="button is-text"
                          @click=${() => this._editInputField(val.id)}
                          title="Edit"
                        >
                          <omni-icon
                            class="is-size-2"
                            icon-id="omni:interactive:edit"
                          ></omni-icon>
                        </button>
                        <button
                          class="button is-text ml-2"
                          @click=${() => this._cloneInputField(val.id)}
                          title="Clone"
                        >
                          <omni-icon
                            class="is-size-2"
                            icon-id="omni:interactive:copy"
                          ></omni-icon>
                        </button>

                        <button
                          class="button is-text ml-2"
                          @click=${() => this._deleteInputField(val.id)}
                          title="Delete"
                        >
                          <omni-icon
                            class="is-size-2"
                            icon-id="omni:interactive:trash"
                          ></omni-icon>
                        </button>
                      </td>
                    `,
                  },
                ]}
                .data="${this.tableData}"
              ></om2-table>

              <om2-icon-menu
                class="is-small"
                iconId="omni:interactive:add"
                iconClass="is-size-2"
                buttonClass="is-outlined mt-1"
                tooltip="Add Input Field"
                .options="${Object.entries(this._filteredInputFields()).map(
                  ([type, field]) => ({
                    label: field.label,
                    value: { type, field },
                  })
                )}"
                @change="${e =>
                  this._addInputField(
                    e.detail.value.value.type,
                    e.detail.value.value.field
                  )}"
              >
              </om2-icon-menu>
            `}
      </div>
    `;
  }

  /**
   * Render child layouts recursively with nested om2-form-builder elements
   *
   * @returns {html} rendered output
   */
  _renderChildren() {
    if (this._meta?.maxContents === 0) return '';

    let children = Array.isArray(this.uiSchema)
      ? this.uiSchema
      : this.uiSchema.content;
    if (!children) return '';
    if (children && !Array.isArray(children)) {
      this.uiSchema.content = [children]; // Fix schema to make content an array
      children = this.uiSchema.content;
    }
    return html`
      ${repeat(
        children,
        child => child,
        (child, i) => html`
          <om2-form-builder
            class="mt-1 ${this.layoutHidden ? 'is-hidden' : ''}"
            type=${child.type ?? ''}
            .schema=${this.schema}
            .uiSchema=${child}
            .sharedState=${this.sharedState}
            .position=${i}
            .maxPosition=${children.length - 1}
            .isRoot=${false}
            .layoutHidden=${false}
            .env=${this.env}
            @move=${e => {
              children.splice(i, 1);
              children.splice(e.detail.position, 0, child);
              this.requestUpdate();
              e.stopPropagation();
            }}
            @delete=${e => {
              // Use indexOf(). If multiple delete events happen simultaneously we can't
              // rely on the value of i as the array indices are changing.
              children.splice(children.indexOf(child), 1);
              this.requestUpdate();
              e.stopPropagation();
            }}
          >
          </om2-form-builder>
        `
      )}

      <div class=${this.layoutHidden ? 'is-hidden' : ''}>
        <om2-icon-menu
          class="is-small"
          iconId="omni:interactive:add"
          iconClass="is-size-2"
          buttonClass="is-outlined mt-1"
          tooltip=${`Add to ${
            this.uiSchema.type ?? 'the base'
          } layout contents`}
          .options="${(
            this._meta?.restrictContents ??
            Object.keys(metadata.layout ?? {}).filter(
              k =>
                !metadata.layout[k].restrictParent ||
                metadata.layout[k].restrictParent.includes(this.uiSchema.type)
            )
          ).map(type => ({
            label: metadata.layout[type].label ?? type,
            value: type,
          }))}"
          @change="${e => this._addLayout(e.detail.value.value)}"
        >
        </om2-icon-menu>
      </div>
    `;
  }

  /**
   * Render preview of form
   *
   * @returns {html} rendered output
   */
  _renderPreview() {
    return html`
      <omni-style>
        <om2-form
          .env="${this.env}"
          .schema="${this.schema}"
          .uiSchema="${this.uiSchema}"
          .save=${() => om2alert('Validation succeeded', { type: 'success' })}
          submitButtonText="Validate Form"
          @om2-form-cancel=${e => {
            e.stopPropagation();
            this.dispatchNewEvent('om2-form-builder-preview-cancel');
          }}
        >
        </om2-form>
      </omni-style>
    `;
  }

  render() {
    if (!this.uiSchema || !this.schema) return '';
    if (this.preview) return this._renderPreview();

    // The pretty type name we use for the current layout object
    let type = this.uiSchema?.type;
    if (this.isRoot) type = 'Layout';
    if (type === 'ref') type = 'input';

    const actions = [
      { label: 'Edit', value: '_editLayout' },
      { label: 'Delete', value: '_deleteLayout' },
    ];
    if (this.maxPosition > 0) {
      actions.push({ label: 'Move', value: '_moveLayout' });
    }

    return html`
      <omni-style class=${type === 'column' ? 'is-inline-block' : ''}>
        ${this._renderInputFields()}
        <div
          class="box ${type === 'column' ? 'is-inline-block' : ''}"
          @mouseover=${e => this._highlightBox(e)}
          @mouseleave=${() => this._highlightBox(null)}
          @focus=${() => {}}
        >
          <om2-icon-menu
            class="is-pulled-right is-right mt-1 is-small ${this.isRoot
              ? 'is-hidden'
              : ''}"
            iconId="omni:interactive:actions"
            iconClass="is-size-3"
            tooltip="Actions"
            .options="${actions}"
            @change="${e => this[e.detail.value.value]()}"
          >
          </om2-icon-menu>

          <span
            class="is-clickable plusminus"
            @click=${() => {
              this.layoutHidden = !this.layoutHidden;
            }}
            @keyup=${e => {
              if (['Enter', ' '].includes(e.key)) {
                this.layoutHidden = !this.layoutHidden;
              }
            }}
            >${this.layoutHidden ? html`&plusb;` : html`&boxminus;`}
          </span>

          <span class="box-type">${type}</span>
          ${this.layoutHidden
            ? ''
            : html`
                ${this.uiSchema[this._meta?.summary] === undefined
                  ? ''
                  : html`<span class="subtitle is-size-4"
                      >${this.uiSchema[this._meta.summary]}</span
                    >`}
                <br />
              `}
          ${this._renderChildren()}
        </div>

        ${this.isRoot
          ? html`
              <div class="buttons is-right mt-2">
                <button @click=${this._cancel} class="button is-text">
                  Cancel
                </button>
                <button
                  @click=${() => this._triggerSave()}
                  class="button is-primary"
                >
                  Save
                </button>
              </div>
            `
          : ''}
      </omni-style>
    `;
  }
}

Om2FormBuilder.register();
